// Educational guide content for SEO + AI-search. Every page is honest, source-able,
// and free of fabricated facts. No prices, lead-time guarantees, reviews, ratings,
// addresses, phone numbers, or unverified provider metrics. Matching claims use the
// approved honest language: founder-reviewed, target 24-48h, subject to availability,
// 10 active markets; Vox Service Manager is worldwide for recordkeeping only.

export const GUIDES_INDEX = {
  title: 'Industrial Service Guides | Vox Meridian',
  description:
    'Plain-English guides on industrial RFQs, calibration, maintenance and contractor compliance — from the team behind the Vox Meridian RFQ marketplace and Service Manager.',
};

export const GUIDES = [
  {
    slug: 'iso-9001-calibration',
    title: 'What is ISO 9001 Calibration and Why Does It Matter? | Vox Meridian',
    description:
      'ISO 9001 calibration means keeping measurement instruments traceable and accurate under a certified quality system. Learn what it requires, why buyers ask for it, and how to track certificates.',
    keyword: 'what is iso 9001 calibration',
    h1: ['What is ISO 9001 calibration,', 'and why does it matter?'],
    lead: 'ISO 9001 calibration is the practice of verifying and adjusting measurement instruments so their readings stay traceable to recognised standards, as required by the ISO 9001 quality-management standard. It matters because inaccurate instruments silently produce out-of-spec parts, failed audits and rejected deliveries.',
    readTime: '6 min read',
    takeaways: [
      'ISO 9001 requires measuring equipment to be calibrated or verified at specified intervals against traceable standards.',
      'Calibration is about confidence in measurement — it protects product quality, audits and customer trust.',
      'Each instrument needs a record: what was calibrated, when, by whom, the result, and the next due date.',
      'Keeping certificates and due dates in one place prevents lapsed calibrations and audit findings.',
    ],
    sections: [
      {
        h: 'What calibration actually means',
        paragraphs: [
          'Calibration is the comparison of an instrument against a known reference to determine and document any deviation. It does not always mean adjustment — it first tells you how far off the reading is. Verification confirms the instrument still meets its required accuracy.',
          'Under ISO 9001, clause 7.1.5 (monitoring and measuring resources) requires that measuring equipment be calibrated or verified at specified intervals, or before use, against standards traceable to international or national measurement standards.',
        ],
        list: [
          'Identify every instrument that affects product quality.',
          'Set a calibration interval based on usage, drift and risk.',
          'Use an accredited or traceable calibration provider.',
          'Record the result and the next due date for each item.',
        ],
      },
      {
        h: 'Why buyers and auditors ask for it',
        paragraphs: [
          'When a part fails in the field, the first question is whether the measurement that approved it can be trusted. Traceable calibration closes that question. Auditors look for a current certificate, a known due date, and evidence that out-of-tolerance results triggered action.',
          'For buyers, a supplier with disciplined calibration is a lower-risk supplier. It signals that dimensions, pressures, torques and temperatures on the certificate of conformance mean something.',
        ],
      },
      {
        h: 'Keeping on top of due dates',
        paragraphs: [
          'The most common calibration failure is not a bad instrument — it is a lapsed date discovered during an audit. Every instrument should live in a register with its last calibration date, next due date, the contractor or lab that did the work, and the certificate itself.',
          'Vox Service Manager keeps calibration records, certificates and contractor contacts in one place and reminds you before each due date, so a renewal never slips. It is free during the pilot and works for companies anywhere in the world.',
        ],
      },
    ],
    faq: [
      {
        q: 'Does ISO 9001 require calibration?',
        a: 'Yes. ISO 9001 clause 7.1.5 requires measuring equipment used to verify conformity to be calibrated or verified at specified intervals against traceable measurement standards.',
      },
      {
        q: 'What is the difference between calibration and verification?',
        a: 'Calibration compares an instrument to a reference and documents the deviation; verification checks that the instrument still meets its required accuracy. Calibration may be followed by adjustment.',
      },
      {
        q: 'How often should instruments be calibrated?',
        a: 'Intervals depend on the instrument, how heavily it is used, its drift history and the risk of a wrong reading. Many firms start with annual intervals and adjust based on as-found results.',
      },
    ],
    cta: {
      primary: { label: 'Find a calibration provider', to: '/buyers?category=Industrial%20Calibration' },
      secondary: { label: 'Track calibration in Service Manager', to: '/service-manager' },
    },
    related: ['contractor-compliance-certificates', 'equipment-maintenance-logs'],
  },
  {
    slug: 'write-industrial-rfq',
    title: 'How to Prepare a Technical RFQ for Industrial Maintenance | Vox Meridian',
    description:
      'A clear technical RFQ gets faster, comparable quotes. Learn what to include — scope, drawings, site access, standards and deadlines — so industrial maintenance providers can price accurately.',
    keyword: 'how to write rfq for industrial maintenance',
    h1: ['How to prepare a technical RFQ', 'for industrial maintenance.'],
    lead: 'A technical RFQ (request for quotation) for industrial maintenance is a document that tells a service provider exactly what work you need, where, to what standard and by when, so they can return an accurate, comparable price. The clearer the RFQ, the faster and more comparable the quotes.',
    readTime: '7 min read',
    takeaways: [
      'Define the scope precisely: the equipment, the fault or task, and what "done" looks like.',
      'Attach drawings, photos and nameplate data so providers quote on facts, not guesses.',
      'State site access, permits, safety rules and working windows up front.',
      'Give a realistic deadline and ask for an itemised quote you can compare line by line.',
    ],
    sections: [
      {
        h: 'Start with a precise scope',
        paragraphs: [
          'The single biggest cause of slow or wildly different quotes is a vague scope. Name the equipment (make, model, serial), describe the symptom or the task, and define the outcome you expect. "Overhaul the cooling-water pump" is weaker than "Remove, overhaul and re-commission pump P-201, including new bearings and mechanical seal, with a post-repair performance test."',
          'If you are unsure of the exact fault, say so and describe what you observe. A good provider will quote a diagnostic visit first.',
        ],
        list: [
          'Equipment: make, model, serial, location.',
          'Task or symptom in plain terms.',
          'The definition of done and any test required.',
          'What is in scope and explicitly out of scope.',
        ],
      },
      {
        h: 'Attach the evidence',
        paragraphs: [
          'Drawings, photos and nameplate data let a provider price on facts. Include P&IDs, GA drawings, wiring diagrams or photos of the installation and the data plate. On Vox Meridian you can upload drawings and specifications directly with your RFQ.',
          'The more a provider can see before a site visit, the fewer assumptions sit inside the price — and the fewer variations appear later.',
        ],
      },
      {
        h: 'State the site conditions',
        paragraphs: [
          'Maintenance is priced against reality: access, permits-to-work, safety inductions, working hours, isolation requirements and whether the area is live. Surprises on site become variations on the invoice.',
          'Tell providers about parking, lifting access, available power and water, and any client-side supervision they must work under.',
        ],
      },
      {
        h: 'Ask for a comparable quote',
        paragraphs: [
          'Ask each provider for an itemised quote: labour, parts, travel, testing and any exclusions. State your currency and a realistic deadline, and give a decision date so providers can plan.',
          'On Vox Meridian you post one RFQ and the founder reviews and matches it to suitable providers by hand, targeting a response within 24 to 48 hours, subject to availability in your market.',
        ],
      },
    ],
    faq: [
      {
        q: 'What should an industrial RFQ include?',
        a: 'Scope of work, equipment details, drawings or photos, site access and safety conditions, the required standard, a deadline, and a request for an itemised quote.',
      },
      {
        q: 'Why do quotes for the same job vary so much?',
        a: 'Usually because the scope was ambiguous, so each provider priced different assumptions. A precise, well-evidenced RFQ produces quotes you can compare line by line.',
      },
      {
        q: 'How long does it take to get quotes?',
        a: 'On Vox Meridian the founder reviews and matches each RFQ by hand, targeting 24 to 48 hours subject to provider availability in your market.',
      },
    ],
    cta: {
      primary: { label: 'Post an RFQ — free', to: '/buyers' },
      secondary: { label: 'See how matching works', to: '/buyers' },
    },
    related: ['find-verified-providers', 'preventative-vs-reactive-maintenance'],
  },
  {
    slug: 'preventative-vs-reactive-maintenance',
    title: 'Preventative vs. Reactive Maintenance: A Cost Comparison | Vox Meridian',
    description:
      'Preventative maintenance schedules work before failure; reactive maintenance repairs after breakdown. Understand the real cost trade-offs and when each approach makes sense.',
    keyword: 'preventative vs reactive maintenance cost',
    h1: ['Preventative vs. reactive maintenance:', 'a cost comparison.'],
    lead: 'Preventative maintenance is planned work carried out on a schedule to stop failures before they happen, while reactive maintenance is repair carried out only after equipment breaks down. Preventative maintenance generally costs less over time for critical equipment because it avoids unplanned downtime, emergency call-outs and secondary damage.',
    readTime: '6 min read',
    takeaways: [
      'Reactive maintenance means unplanned downtime, emergency rates and possible secondary damage.',
      'Preventative maintenance spreads cost predictably and protects critical assets.',
      'The right mix depends on how critical the equipment is and the cost of a failure.',
      'A simple maintenance register with due dates makes a preventative plan actually happen.',
    ],
    sections: [
      {
        h: 'What each approach really is',
        paragraphs: [
          'Reactive maintenance (also called run-to-failure or breakdown maintenance) waits until something stops working, then repairs it. Preventative maintenance performs inspections, servicing and part replacement on a time- or usage-based schedule, before failure.',
          'Neither is universally right. The correct choice depends on the cost of a failure versus the cost of preventing it.',
        ],
        list: [
          'Reactive: no planned cost, but unpredictable downtime and emergency pricing.',
          'Preventative: predictable scheduled cost, less unplanned downtime.',
          'Condition-based: service triggered by measured condition, sits between the two.',
        ],
      },
      {
        h: 'The true cost of a breakdown',
        paragraphs: [
          'The invoice for the repair is only part of the cost of a failure. Add lost production, idle labour, expedited freight for parts, emergency call-out premiums, and any damage the failure causes to surrounding equipment or product.',
          'For a critical line, a single unplanned stoppage can cost more than a year of scheduled servicing. For non-critical, easily replaced equipment, run-to-failure can be the cheaper strategy.',
        ],
      },
      {
        h: 'Choosing the right mix',
        paragraphs: [
          'Rank equipment by criticality: what stops production, what creates a safety or compliance risk, and what is cheap and quick to replace. Apply preventative maintenance to the first two groups and consider reactive for the last.',
          'Whatever the mix, the plan only works if it is tracked. Vox Service Manager holds your maintenance records, service history and contractor contacts, and reminds you before each due date — free during the pilot, for companies anywhere in the world.',
        ],
      },
    ],
    faq: [
      {
        q: 'Is preventative maintenance cheaper than reactive?',
        a: 'For critical equipment, usually yes, because it avoids unplanned downtime, emergency rates and secondary damage. For cheap, non-critical, easily replaced items, run-to-failure can cost less.',
      },
      {
        q: 'What is reactive maintenance?',
        a: 'Reactive maintenance is repair performed only after equipment fails, with no scheduled servicing beforehand.',
      },
      {
        q: 'How do I start a preventative maintenance plan?',
        a: 'List your equipment, rank it by criticality, set a service interval for the critical items, and record each visit and its next due date in a maintenance register.',
      },
    ],
    cta: {
      primary: { label: 'Track maintenance in Service Manager', to: '/service-manager' },
      secondary: { label: 'Find a maintenance provider', to: '/buyers' },
    },
    related: ['equipment-maintenance-logs', 'write-industrial-rfq'],
  },
  {
    slug: 'contractor-compliance-certificates',
    title: 'The Ultimate Guide to Managing Contractor Compliance Certificates | Vox Meridian',
    description:
      'Contractor compliance certificates prove your service providers are licensed, insured and qualified. Learn which documents to collect, how to track expiry dates, and how to avoid lapsed cover.',
    keyword: 'how to manage contractor compliance certificates',
    h1: ['Managing contractor compliance', 'certificates — the complete guide.'],
    lead: 'Contractor compliance certificate management is the process of collecting, verifying and tracking the documents that prove your service providers are licensed, insured and qualified — and making sure none of them quietly expire. It protects you from liability, failed audits and uninsured work on your site.',
    readTime: '7 min read',
    takeaways: [
      'Collect proof of licensing, insurance and qualifications before any contractor starts work.',
      'Every certificate has an expiry date — track them centrally, not in inboxes.',
      'Lapsed insurance or licences transfer risk onto you.',
      'Set reminders ahead of expiry so renewals never slip.',
    ],
    sections: [
      {
        h: 'Which certificates to collect',
        paragraphs: [
          'The exact documents depend on your country and trade, but the principle is constant: verify that the contractor is legally allowed to do the work and is insured if something goes wrong. Collect these before work begins, not after.',
        ],
        list: [
          'Business registration and any trade licence required locally.',
          'Public liability and, where relevant, professional indemnity insurance.',
          'Worker insurance or work injury compensation cover.',
          'Trade-specific qualifications and certifications for the personnel on site.',
        ],
      },
      {
        h: 'Why expiry dates are the real risk',
        paragraphs: [
          'A certificate that was valid when you first engaged a contractor can lapse months later while work continues. If an uninsured or unlicensed contractor has an incident on your site, the liability can land on you.',
          'The fix is administrative, not technical: record each certificate with its expiry date and get reminded before it lapses.',
        ],
      },
      {
        h: 'Building a simple tracking system',
        paragraphs: [
          'Keep every contractor and their documents in one register: the document type, issue date, expiry date and the file itself. Review upcoming expiries monthly and chase renewals before they fall due.',
          'Vox Service Manager stores contractor contacts, certificates and reports in one place and reminds you at 30, 14 and 7 days before a due date, and again on the day. It is free during the pilot and works for companies anywhere in the world.',
        ],
      },
    ],
    faq: [
      {
        q: 'What certificates should I ask a contractor for?',
        a: 'Ask for business registration, any required trade licence, public liability insurance, worker injury cover, and trade-specific qualifications for the personnel doing the work.',
      },
      {
        q: 'Why track certificate expiry dates?',
        a: 'Because a certificate valid at engagement can lapse while work continues, leaving you exposed to liability, failed audits and uninsured incidents.',
      },
      {
        q: 'How do I stop certificates expiring unnoticed?',
        a: 'Record each certificate with its expiry date in a central register and set reminders ahead of the date so renewals are chased in time.',
      },
    ],
    cta: {
      primary: { label: 'Track certificates in Service Manager', to: '/service-manager' },
      secondary: { label: 'Find a verified provider', to: '/buyers' },
    },
    related: ['iso-9001-calibration', 'equipment-maintenance-logs'],
  },
  {
    slug: 'find-verified-providers',
    title: 'How Long Does It Take to Find Verified Industrial Service Providers? | Vox Meridian',
    description:
      'Finding a verified industrial service provider can take days of cold calls — or one reviewed RFQ. Here is an honest look at how Vox Meridian matches buyers with founder-reviewed providers.',
    keyword: 'time to find industrial service providers',
    h1: ['How long does it take to find', 'verified industrial service providers?'],
    lead: 'Finding a verified industrial service provider the traditional way — searching, cold-calling and vetting one firm at a time — typically takes days to weeks. On Vox Meridian you post one RFQ and the founder reviews and matches it by hand, targeting a response within 24 to 48 hours, subject to provider availability in your market.',
    readTime: '5 min read',
    takeaways: [
      'The traditional search-and-cold-call route often takes days to weeks per provider.',
      'One well-written RFQ can reach several vetted providers at once.',
      'On Vox Meridian every match is reviewed by the founder, not an unattended algorithm.',
      'We target 24-48 hours, subject to availability — and we tell you honestly if a trade needs more sourcing time.',
    ],
    sections: [
      {
        h: 'Why the traditional route is slow',
        paragraphs: [
          'Finding a provider yourself means searching directories, calling firms that may not do your trade, waiting for callbacks, and then checking that each one is registered, insured and capable. You repeat that loop for every provider you want to compare.',
          'Most of the time goes into vetting and chasing, not into the actual work.',
        ],
      },
      {
        h: 'How founder-reviewed matching works',
        paragraphs: [
          'On Vox Meridian you post a single RFQ describing the job. The founder reads it, and matches it to providers whose services, market and delivery mode fit. Providers are reviewed for business registration and capability before they ever see your drawings.',
          'This is a concierge process, not an automated blast. It is deliberately human, because a good match beats a fast bad one.',
        ],
        list: [
          'You post one RFQ with scope, drawings and deadline.',
          'The founder reviews it and selects suitable providers.',
          'Matched providers respond; you compare comparable quotes.',
          'You choose; the job is tracked through to completion.',
        ],
      },
      {
        h: 'An honest word on timing and coverage',
        paragraphs: [
          'We target a first response within 24 to 48 hours, but that depends on provider availability in your market. Vox Meridian actively operates across 10 markets, and coverage varies by trade and country.',
          'If your trade or location needs more sourcing time, we tell you straight rather than leaving you waiting on a match that is not there yet.',
        ],
      },
    ],
    faq: [
      {
        q: 'How fast will I get quotes on Vox Meridian?',
        a: 'The founder targets a first response within 24 to 48 hours of reviewing your RFQ, subject to provider availability in your market.',
      },
      {
        q: 'Are providers verified?',
        a: 'Providers are reviewed for business registration and capability before they see your RFQ. Matching itself is reviewed by the founder, not left to an unattended algorithm.',
      },
      {
        q: 'What if there is no provider in my country?',
        a: 'We will tell you honestly if your trade or market needs more sourcing time, rather than promise a match we cannot yet make.',
      },
    ],
    cta: {
      primary: { label: 'Post an RFQ — free', to: '/buyers' },
      secondary: { label: 'Join as a provider', to: '/providers' },
    },
    related: ['write-industrial-rfq', 'preventative-vs-reactive-maintenance'],
  },
  {
    slug: 'equipment-maintenance-logs',
    title: 'Essential Maintenance Logs for Manufacturing Equipment | Vox Meridian',
    description:
      'A good equipment maintenance log records every service, repair and due date in one place. Learn what to log, why it protects value and uptime, and how to keep it effortlessly up to date.',
    keyword: 'manufacturing equipment maintenance logs',
    h1: ['Essential maintenance logs', 'for manufacturing equipment.'],
    lead: 'An equipment maintenance log is a running record of every service, inspection, repair and due date for a machine, kept in one place over its life. A good log protects uptime, supports warranty and resale value, and proves to auditors and buyers that the equipment was looked after.',
    readTime: '6 min read',
    takeaways: [
      'Log every service, repair and inspection against each machine, not just the big jobs.',
      'Record the date, the contractor, the work done, and the next due date.',
      'Attach reports and certificates so evidence lives with the record.',
      'A central log with reminders turns a maintenance plan into actual maintenance.',
    ],
    sections: [
      {
        h: 'What to record for each machine',
        paragraphs: [
          'For every item of equipment, keep a single history that grows over its life. Each entry should capture enough that someone new could understand what was done and what happens next, without asking the person who did it.',
        ],
        list: [
          'Date of the service, inspection or repair.',
          'Who did the work (in-house or which contractor).',
          'What was done, parts replaced, and the result.',
          'The next due date and any report or certificate file.',
        ],
      },
      {
        h: 'Why logs pay for themselves',
        paragraphs: [
          'A complete log turns maintenance from memory into evidence. It reveals recurring faults, supports warranty claims, proves compliance in audits, and adds real value when you sell or lease the machine — buyers pay more for equipment with a documented history.',
          'It also stops the most expensive failure of all: a service that was due but nobody remembered.',
        ],
      },
      {
        h: 'Keeping the log up to date without the admin',
        paragraphs: [
          'The reason logs fall behind is that updating them is a chore. The answer is a single register where adding a visit takes seconds and the next due date is set automatically.',
          'Vox Service Manager is built exactly for this: add equipment, log each visit with its report or certificate, and get reminded at 30, 14 and 7 days before the next due date. It is free during the pilot and works for companies anywhere in the world.',
        ],
      },
    ],
    faq: [
      {
        q: 'What is an equipment maintenance log?',
        a: 'A maintenance log is a running record of every service, inspection and repair for a machine, including dates, the contractor, the work done, the result and the next due date.',
      },
      {
        q: 'Why keep maintenance logs?',
        a: 'Logs protect uptime, reveal recurring faults, support warranty and audit compliance, and increase resale value by proving the equipment was maintained.',
      },
      {
        q: 'How do I keep a maintenance log up to date?',
        a: 'Use one central register where logging a visit takes seconds and the next due date is captured each time, with reminders so nothing is missed.',
      },
    ],
    cta: {
      primary: { label: 'Start logging in Service Manager', to: '/service-manager' },
      secondary: { label: 'Book a service visit', to: '/buyers' },
    },
    related: ['preventative-vs-reactive-maintenance', 'contractor-compliance-certificates'],
  },
];

export const findGuide = (slug) => GUIDES.find((g) => g.slug === slug);
